import type { EnvKind, Environment, Project } from '@hangar/shared';

/*
 * Pure side of the environment helpers (no `ApiError`, so nothing here touches the auth client): ordering, labels and
 * the blockers of a refused change. environmentHelpers.ts re-exports all of it next to `blockersOf`.
 */

/** Live environments of a project in promotion order (`sortOrder`, then creation). */
export const liveEnvironments = (project: Project): Environment[] => project.environments.filter((e) => !e.archivedAt).sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
export const archivedEnvironments = (project: Project): Environment[] => project.environments.filter((e) => Boolean(e.archivedAt)).sort((a, b) => a.sortOrder - b.sortOrder);

export const ENV_KINDS: EnvKind[] = ['development', 'preview', 'staging', 'preprod', 'production'];
export const ENV_KIND_LABEL: Record<EnvKind, string> = { development: 'Development', preview: 'Preview', staging: 'Staging', preprod: 'Pre-production', production: 'Production' };

/** `untrustedRefsBlocked` as the design's select says it. */
export const FORKS_LABEL = { blocked: 'Block — never trigger builds', allowed: 'Allow — forks and external pull requests may build' } as const;

/** One `details.blockers[]` entry of a 409 (archive or slug change refused): what still uses the environment. */
export interface Blocker {
  kind: string;
  count: number;
  items: Record<string, unknown>[];
}

const BLOCKER_LABEL: Record<string, [string, string]> = {
  active_builds: ['active build', 'active builds'],
  env_vars: ['environment variable', 'environment variables'],
  profile_bindings: ['build profile binding', 'build profile bindings'],
  builds: ['build', 'builds'],
  shadow_runs: ['shadow run', 'shadow runs'],
  env_requirements: ['env requirement', 'env requirements'],
  env_assertions: ['env assertion', 'env assertions'],
};

export const blockerLabel = (b: Blocker): string => {
  const [one, many] = BLOCKER_LABEL[b.kind] ?? [b.kind.replace(/_/g, ' '), b.kind.replace(/_/g, ' ')];
  return `${b.count} ${b.count === 1 ? one : many}`;
};

/** A few names of what a blocker lists (`storeai-mobile#1287`, `EXPO_PUBLIC_API_URL`, `storeai-mobile/store`). */
export function blockerSample(b: Blocker): string[] {
  return b.items.slice(0, 4).map((i) => {
    if (typeof i.ref === 'string') return i.ref;
    if (typeof i.key === 'string') return i.key;
    if (typeof i.app === 'string' && typeof i.profile === 'string') return `${i.app}/${i.profile}`;
    if (typeof i.app === 'string' && typeof i.day === 'string') return `${i.app} ${i.day}`;
    return typeof i.id === 'string' ? i.id.slice(0, 8) : '';
  }).filter(Boolean);
}
